import React, { useEffect, useState } from "react";
import Header from "../../components/default/Header";
import LoaderForPage from "../../components/LoaderForPage";
import Footer from "../../components/default/Footer";
import StoryTitle from "../../components/home/StoryTitle";
import { FaShareAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import StoriesService from "../../services/StoriesService";
import { useTranslation } from "react-i18next";
import { shareSite } from "../../utils/shareSite";

const STORIES_CACHE_KEY = "stories-cache-v1";
const STORIES_CACHE_TTL_MS = 15 * 60 * 1000;
const INITIAL_VISIBLE_STORIES = 6;

const getPrefetchedStories = () => {
  if (typeof window === "undefined") {
    return [];
  }

  return Array.isArray(window.__storiesPrefetchData)
    ? window.__storiesPrefetchData
    : [];
};

const getCachedStories = () => {
  try {
    const cachedStories = JSON.parse(
      localStorage.getItem(STORIES_CACHE_KEY) || "null"
    );

    if (!cachedStories?.savedAt || !Array.isArray(cachedStories?.data)) {
      return [];
    }

    if (Date.now() - cachedStories.savedAt > STORIES_CACHE_TTL_MS) {
      return [];
    }

    return cachedStories.data;
  } catch (error) {
    return [];
  }
};

const cacheStories = (stories) => {
  try {
    localStorage.setItem(
      STORIES_CACHE_KEY,
      JSON.stringify({
        data: stories,
        savedAt: Date.now(),
      })
    );
  } catch (error) {
    // Ignore localStorage write failures and keep the network response.
  }
};

const Home = () => {
  const { t } = useTranslation();
  const initialStories = getPrefetchedStories().length
    ? getPrefetchedStories()
    : getCachedStories();
  const [stories, setStories] = useState(initialStories);
  const [isLoading, setIsLoading] = useState(initialStories.length === 0);
  const [visibleStoriesCount, setVisibleStoriesCount] = useState(
    initialStories.length > 0
      ? Math.min(initialStories.length, INITIAL_VISIBLE_STORIES)
      : INITIAL_VISIBLE_STORIES
  );

  const watchedStories = JSON.parse(
    localStorage.getItem("watchedStories") || "[]"
  );

  const getStoryStatus = (id) => {
    if (watchedStories.includes(id)) {
      return "Watched";
    }

    return "New";
  };

  const handleShare = async () => {
    try {
      const shareResult = await shareSite({
        title: t("storiesForLife"),
        text: t("storiesForLife"),
        url: window.location.origin,
      });

      if (shareResult === "copied") {
        toast.success("Link copied to clipboard.");
      }
    } catch (error) {
      if (error?.name !== "AbortError") {
        toast.error(error.message);
      }
    }
  };

  useEffect(() => {
    let isMounted = true;

    (async () => {
      try {
        let response = getPrefetchedStories();

        if (response.length === 0 && window.__storiesPrefetch) {
          const prefetchedStories = await window.__storiesPrefetch;
          response = Array.isArray(prefetchedStories) ? prefetchedStories : [];
        }

        if (response.length === 0) {
          response = await StoriesService.getStories();
        }

        if (!isMounted || !Array.isArray(response)) {
          return;
        }

        setStories(response);
        cacheStories(response);
      } catch (error) {
        console.error(error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (stories.length === 0) {
      return;
    }

    setVisibleStoriesCount(Math.min(stories.length, INITIAL_VISIBLE_STORIES));
  }, [stories]);

  useEffect(() => {
    if (stories.length <= INITIAL_VISIBLE_STORIES) {
      return;
    }

    const revealAllStories = () => {
      setVisibleStoriesCount(stories.length);
    };

    if ("requestIdleCallback" in window) {
      const idleCallbackId = window.requestIdleCallback(revealAllStories, {
        timeout: 1200,
      });

      return () => {
        window.cancelIdleCallback(idleCallbackId);
      };
    }

    const timeoutId = window.setTimeout(revealAllStories, 400);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [stories]);

  const visibleStories = stories.slice(0, visibleStoriesCount);
  const allStoriesVisible = visibleStoriesCount >= stories.length;

  return (
    <div>
      <Header />

      {(stories?.length ?? 0) === 0 && !isLoading ? (
        <div>There are no any stories yet.</div>
      ) : (
        <div>
          <div className="custom-scrollbar-container">
            <div className="p-3" style={{ direction: "ltr" }}>
              <div className="text-[14px] text-primary text-right font-semibold mt-4">
                {t("stories")}
              </div>
              {isLoading && (
                <div className="flex justify-center items-center">
                  <LoaderForPage />
                </div>
              )}
              {visibleStories.map((story, index) => (
                <div key={story.id ?? index}>
                  <StoryTitle
                    title={story.title}
                    subtitle={story.subtitle}
                    watchStatus={getStoryStatus(story.id)}
                    thumbnailUrl={story.thumbnailUrl}
                    id={story.id}
                    order={story.order}
                    priorityThumbnail={index === 0}
                  />
                </div>
              ))}
              {allStoriesVisible && (
                <button
                  type="button"
                  onClick={handleShare}
                  className="h-[70px] w-full bg-white border-0 shadow-md rounded-[16px] mt-4 flex justify-end items-center p-2.5"
                >
                  <div className="text-[16px] font-semibold">{t("share")}</div>
                  <div className="text-primary ms-3">
                    <div className="w-6">
                      <FaShareAlt className="m-auto text-[20px] text-primary " />
                    </div>
                  </div>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <Footer t={t} />
    </div>
  );
};

export default Home;
